import { useEffect } from "react";

/**
 * useSeo — lightweight per-page SEO meta manager.
 * Googlebot renders JS, so updating <title>/<meta> on mount is sufficient
 * for indexing blog routes without SSR.
 */
export default function useSeo({
    title,
    description,
    canonical,
    keywords,
    image,
    type = "article",
    jsonLd,
} = {}) {
    useEffect(() => {
        if (title) document.title = title;

        const setMeta = (selector, attr, value) => {
            if (!value) return;
            let el = document.head.querySelector(selector);
            if (!el) {
                el = document.createElement("meta");
                const [key, val] = selector
                    .replace(/[[\]"]/g, "")
                    .split("=");
                el.setAttribute(key, val);
                document.head.appendChild(el);
            }
            el.setAttribute(attr, value);
        };

        setMeta('meta[name="description"]', "content", description);
        setMeta('meta[name="keywords"]', "content", keywords);
        setMeta('meta[property="og:title"]', "content", title);
        setMeta('meta[property="og:description"]', "content", description);
        setMeta('meta[property="og:type"]', "content", type);
        setMeta('meta[property="og:url"]', "content", canonical);
        setMeta('meta[property="og:image"]', "content", image);
        setMeta('meta[name="twitter:title"]', "content", title);
        setMeta('meta[name="twitter:description"]', "content", description);
        setMeta('meta[name="twitter:image"]', "content", image);

        if (canonical) {
            let link = document.head.querySelector('link[rel="canonical"]');
            if (!link) {
                link = document.createElement("link");
                link.setAttribute("rel", "canonical");
                document.head.appendChild(link);
            }
            link.setAttribute("href", canonical);
        }

        // JSON-LD (per-page). Tagged so we can replace on navigation.
        const existing = document.head.querySelector(
            'script[data-seo-jsonld="page"]',
        );
        if (existing) existing.remove();
        if (jsonLd) {
            const s = document.createElement("script");
            s.type = "application/ld+json";
            s.setAttribute("data-seo-jsonld", "page");
            s.text = JSON.stringify(jsonLd);
            document.head.appendChild(s);
        }

        return () => {
            const tag = document.head.querySelector(
                'script[data-seo-jsonld="page"]',
            );
            if (tag) tag.remove();
        };
    }, [title, description, canonical, keywords, image, type, jsonLd]);
}
